/**
 * Navigation as DATA (00-contracts §14.3, principle 9).
 *
 * This is the **only** place the menu is defined: no JSX, no icon imports, no conditionals baked
 * into markup. Adding a Phase 1 page is a `status` flip here plus a real route in
 * `route-tree.ts` — the shell, the guards and the layout are untouched.
 *
 * REKKEFØLGEN ER ATLES (A24, 22. oktober 2026). Et nytt punkt legges inn på sin plass i den, ikke
 * bakerst og ikke ved en flytting av de andre.
 *
 * Over streken, likt for alle, uten overskrift: Oversikt, Innboks, Kontakter, Oppgaver, Kalender, Mine
 * vakter (269.1 b), Dokumenter, Innstillinger. Under streken det som kommer fra moduler, tilgang og
 * deling: Booking, Artister, Spillesteder, Konserter, Mine konserter, Festivaler (og Spillested),
 * Control Room for en plattformadmin, delte artister (A9) og delte produksjoner (§5.1).
 */
import type { Action, ActorRole, OrganizationModule } from '@tvitvi/shared';
import type { LinkProps } from '@tanstack/react-router';

import type { IconName } from '@/components/ui/icons';
import type { TKey } from '@/i18n/types';

export type NavStatus =
  /** Route exists and does something. */
  | 'available'
  /** Visible in the menu, navigable, page says "kommer senere". */
  | 'comingSoon';

export interface NavEntry {
  /** Matches route.staticData.navId. Also the i18n key suffix and the test id. */
  id: string;
  labelKey: TKey;
  icon: IconName;
  to: LinkProps['to'];
  status: NavStatus;
  /**
   * Cosmetic only. The backend is the authority (principle 7); hiding an item the user cannot
   * use is UX, never security.
   */
  permission?: Action;
  /** Rendered as a keyboard hint and registered with useHotkey. */
  shortcut?: string;
  /**
   * MODULEN SPØRSMÅLET STILLES INNE I (G1, QA13). Bare når ressursen bak `permission` deles av flere
   * moduler: Spillesteder og spillestedets saler spør begge `venue:list`. Det er ikke en egen regel
   * ved siden av tilgangen — `can()` tar den som kontekst og sjekker den mot den samme tabellen
   * (`RESOURCE_MODULES`) som API-et. Utelatt: modulen følger av ressursen alene.
   *
   * `kinds`, `module` og `orWithSoldGigs` sto her til G1. Typen er ingen regel lenger, modulen er
   * det `can()` spør, og F24 (byrå med solgte jobber ser Konserter) følger av at Byrå tar med
   * Konserter (253.58).
   */
  inModule?: OrganizationModule;
  /**
   * STÅR I EN PERSONLIG KONTO (sak 216). En personlig konto er en kontotype, ikke en kunde: den har
   * sitt eget lille sett — Oversikt, Innboks, Oppgaver, Kalender og egne konserter — og ingenting
   * som fører en organisasjon. Utelatt: ikke i en personlig konto.
   */
  personalAccount?: true;
  /**
   * AKTØRER SOM ALDRI SER OPPFØRINGEN, uansett matrise — plattformsperren «en gjest ser aldri mer
   * enn det som er delt» (QA runde 5, H1/H2; sak 232). Bare for organisasjonens egne flater.
   */
  hiddenFor?: readonly ActorRole[];
  /**
   * Vises bare når personen har noe her (sak 216): «Konserter» er personens egne, ikke husets, og en
   * festivalsjef som aldri står på et reisefølge skal ikke møte en tom side i menyen.
   */
  onlyWith?: 'concerts' | 'shifts' | 'sharedArtists' | 'sharedProductions';
  /**
   * BARE FOR EN PLATTFORMADMIN (sak 238): Control Room. Følger personen, ikke huset — den står i
   * menyen i hvilken organisasjon som helst når `me.isPlatformAdmin` er sann, og ellers ingen
   * steder. Ingen `permission`: en plattformadmin er ingen rolle i et hus. Ruta og API-et nekter
   * alle andre uansett (404).
   */
  onlyPlatformAdmin?: true;
}

export interface NavSectionDef {
  id: string;
  titleKey?: TKey;
  /**
   * 'static' renders `entries`; 'festivals' renders the org's festivals from the API; 'venues'
   * renders its venues (kind `venue`) as «Spillested», each with the venue submenu (agent A).
   */
  source: 'static' | 'festivals' | 'venues';
  entries: NavEntry[];
  /**
   * FOR SEKSJONENE SOM BYGGES AV DATA (Festivaler, Spillested): spørsmålet som åpner seksjonen, og
   * modulen det stilles inne i (G1). `festival:list` hører til både Festival og Spillested, så
   * modulen sier hvilken av dem seksjonen er.
   */
  permission?: Action;
  inModule?: OrganizationModule;
  /**
   * 'common' er toppblokken, lik for alle i huset. 'house' er alt under skillelinja — modulene, det
   * personen har tilgang til (Mine konserter, Control Room) og det som er delt med personen (A24).
   * Begge ligger i den samme rullende `<nav>`.
   */
  zone: 'common' | 'house';
}

export const NAV_SECTIONS: readonly NavSectionDef[] = [
  /*
   * OVER STREKEN, LIKT FOR ALLE, I ATLES REKKEFØLGE (A24, 22. oktober 2026; 269.1 b): Oversikt, Innboks,
   * Kontakter, Oppgaver, Kalender, Mine vakter, Dokumenter, Innstillinger. Én blokk uten overskrift. Den
   * personlige gruppen fra 253.77 er borte: Mine vakter står rett etter Kalender, og Mine konserter står
   * under streken sammen med Konserter. Nye punkter legges inn på sin plass i denne rekkefølgen.
   */
  {
    id: 'work',
    source: 'static',
    entries: [
      {
        id: 'overview',
        personalAccount: true,
        labelKey: 'nav.overview',
        icon: 'overview',
        to: '/',
        // Flipped when the Phase 1 page landed (byggeplan 1.4). No `permission`: Overview is the
        // landing page every signed-in user meets and there is no `overview:read` cell to ask
        // about — being signed in IS the check, which `navigation.test.ts` names in `UNIVERSAL`
        // rather than hides. What the page shows still varies by role: each panel gates on its
        // own action, and the activity panel is absent below `admin` (contract C-44).
        status: 'available',
        /* Gjestemodus (sak 232, T5): ingen faste menypunkter — gjesten navigerer i det delte. */
        hiddenFor: ['guest'],
        shortcut: 'g o',
      },
      {
        id: 'inbox',
        personalAccount: true,
        labelKey: 'nav.inbox',
        icon: 'inbox',
        to: '/inbox',
        status: 'available',
        /* Gjestemodus (sak 232, T5): ingen faste menypunkter — gjesten navigerer i det delte. */
        hiddenFor: ['guest'],
        // Phase 1, page 3. Same question the route's beforeLoad asks (decision 0005).
        permission: 'thread:list',
        shortcut: 'g i',
      },
      {
        id: 'contacts',
        labelKey: 'nav.contacts',
        icon: 'contacts',
        to: '/contacts',
        status: 'available',
        /* Gjestemodus (sak 232, T5): ingen faste menypunkter — gjesten navigerer i det delte. */
        hiddenFor: ['guest'],
        // Phase 1, page 1. The action that OPENS the page, not the one that edits it:
        // `Sidebar` asks `can()` this exact question, and the route's `beforeLoad` asks it again
        // for anyone who types the address (decision 0005).
        permission: 'contact:list',
        shortcut: 'g c',
        /*
         * Ikke i en personlig konto (sak 216), som ikke fører noe register. Tvitvi-organisasjonen har
         * dem som alle andre siden 253.69 — typen `platform` er ingen regel lenger (G1).
         */
      },
      {
        id: 'tasks',
        personalAccount: true,
        labelKey: 'nav.tasks',
        icon: 'tasks',
        to: '/tasks',
        status: 'available',
        /* Gjestemodus (sak 232, T5): ingen faste menypunkter — gjesten navigerer i det delte. */
        hiddenFor: ['guest'],
        // Phase 1, page 2. Same question the route's beforeLoad asks (decision 0005).
        permission: 'task:list',
        shortcut: 'g t',
      },
      // --- Phase 1 insertion point: work section ---
      {
        id: 'calendar',
        personalAccount: true,
        labelKey: 'nav.calendar',
        icon: 'calendar',
        to: '/calendar',
        status: 'available',
        /* Gjestemodus (sak 232, T5): ingen faste menypunkter — gjesten navigerer i det delte. */
        hiddenFor: ['guest'],
        /*
         * `event:list` FRA 18. SEPTEMBER (rettelse B12), og avsnittet som sto her er grunnen til
         * at det måtte skje.
         *
         * Det lød: «`task:list` and NOT a `calendar:*` cell of its own: there is no such cell,
         * because there is no such resource.» Det stemte da det ble skrevet — kalenderen var et
         * månedsrutenett med oppgaver talt per dag. Den tegner nå møter, skift, tildelinger og
         * programposter, så cella svarte på et annet spørsmål enn det som ble stilt, og lånet lakk
         * begge veier: et hus som skrudde av oppgaver stengte kalenderen samtidig.
         *
         * Atle, 17. september: kalenderen får sin egen celle, standard = `task:*`. Standarden er
         * det som gjør endringen trygg — ingen ser noe annet enn i går før noen flytter en bryter.
         */
        permission: 'event:list',
        // `g k` for kalender: `g c` is Kontakter and `g t` is Oppgaver. A mnemonic in the
        // reader's language, which is the language the menu is in.
        shortcut: 'g k',
      },
      /*
       * Vaktene du er satt på i dette huset, fra i dag og framover (201.14). `shift:list` er spørsmålet
       * siden stiller; en ekstern uten den fikk en feilside fra avataren (QA runde 5, M6).
       */
      {
        id: 'myShifts',
        labelKey: 'myShifts.title',
        icon: 'clock',
        to: '/my-shifts',
        status: 'available',
        hiddenFor: ['guest'],
        permission: 'shift:list',
        onlyWith: 'shifts',
      },
      {
        id: 'documents',
        labelKey: 'nav.documents',
        icon: 'documents',
        to: '/documents',
        status: 'available',
        /* Gjestemodus (sak 232, T5): ingen faste menypunkter — gjesten navigerer i det delte. */
        hiddenFor: ['guest'],
        // The page's own question, per decision 0005: the menu asks what the destination asks.
        // `document:list` is `yes` for three roles and `dept` for `limited`, so this hides the
        // entry from nobody — a reader with no departments still has the open folders.
        permission: 'document:list',
        /*
         * Ikke i en personlig konto (sak 216), som ikke fører noe register. Tvitvi-organisasjonen har
         * dem som alle andre siden 253.69 — typen `platform` er ingen regel lenger (G1).
         */
      },
      /*
       * ORGANISATION SETTINGS, IN THE FOLDER STRUCTURE WHERE THEY BELONG.
       *
       * Until 28 August these were reached from the user menu behind your own avatar, which put
       * the permission matrix — a rule binding everybody in the organisation — in the same place
       * as your own name and language. Atle's call: the account stays behind the avatar, anything
       * about the organisation moves here.
       *
       * Last in the top block on purpose. Settings is where you go when you already know what you
       * want to change, not something you browse on the way to work.
       */
      {
        id: 'settings',
        labelKey: 'nav.settings',
        icon: 'settings',
        to: '/settings',
        status: 'available',
        hiddenFor: ['guest'],
        /*
         * Ikke i en personlig konto (sak 216), som ikke fører noe register. Tvitvi-organisasjonen har
         * dem som alle andre siden 253.69 — typen `platform` er ingen regel lenger (G1).
         */
        /*
         * The landing page's permission, not the section's — `/settings` has no page of its own
         * and forwards to departments. `department:list` is `yes` for all five roles, so this
         * hides the entry from nobody today; it is named rather than left out because decision
         * 0005 wants the menu to ask the same question the destination asks, and an entry with no
         * permission hides from nobody even when the page refuses.
         *
         * The simplification to revisit: if a role ever loses `department:list` it also loses the
         * doorway to tags. That becomes wrong the day `/settings` grows a real index page.
         */
        permission: 'department:list',
      },
    ],
    zone: 'common',
  },
  /*
   * HUSETS FUNKSJONER (Atle, 29.09): «alle options du kan ha når man setter opp hvilke funksjoner
   * hver org skal ha på Tvitvi». Ingen overskrift — skillelinja i `Sidebar` sier at dette er en ny
   * blokk, og Festivaler og Spillested under har sine egne.
   */
  {
    id: 'house',
    source: 'static',
    zone: 'house',
    entries: [
      /*
       * THERE IS NO «Organisasjon» ENTRY, and its absence is the specification rather than an
       * omission. NAVIGATION.md: «Organization er ikke et eget menypunkt. Det er en fane inne i
       * Contacts. […] Samme person skal aldri finnes to steder.»
       *
       * The entry existed as a KOMMER shell while Contacts was being built, and it outlived the
       * tab that answered it — a menu item pointing at an empty page for something the product
       * already does, which is worse than no entry at all: it teaches the reader to look in the
       * wrong place.
       */
      {
        id: 'booking',
        labelKey: 'nav.booking',
        icon: 'booking',
        to: '/booking',
        status: 'available',
        hiddenFor: ['guest'],
        /*
         * «Se Booking» (G2, 0923), og modulen Booking følger av ressursen (G1). Registeret er
         * festivalens egen liste over hvem den vurderer; et byrå har sin roster under Artister.
         */
        permission: 'booking:list',
      },
      /*
       * ARTISTER — byråets motstykke til festivalens Booking (vedtak 93 og 98.1).
       *
       * Atle, 8. september: «Artister skal inneholde alle artistene for organisasjonen i en
       * tabell.» Den står PÅ SAMME PLASS som Booking og aldri sammen med den: de to svarer på det
       * samme spørsmålet fra hver sin side av bordet — hvem er dette huset opptatt av.
       */
      {
        id: 'artists',
        labelKey: 'nav.artists',
        icon: 'booking',
        to: '/artists',
        status: 'available',
        /* Gjestemodus (sak 232, T5): ingen faste menypunkter — gjesten navigerer i det delte. */
        hiddenFor: ['guest'],
        /* «Se artistene» (G2, 0923); modulen Byrå følger av ressursen (G1). */
        permission: 'artist:list',
      },
      /*
       * SPILLESTEDER (SPILLESTED-KONSERT §2): salene andre hus har lagt ut, som en egen side og ikke
       * under Booking. For festival- og byråhus; et personlig hus arrangerer ingenting.
       */
      {
        id: 'venues',
        labelKey: 'nav.venues',
        icon: 'venue',
        to: '/venues',
        status: 'available',
        permission: 'venue:list',
        /* Modulen Spillesteder (253.59, 0922). `venue:list` er også spillestedets egne saler. */
        inModule: 'venues',
        hiddenFor: ['guest'],
      },
      /*
       * HUSETS KONSERTER (agent D sin side). Modulen Konserter (0483). Før modulene ble valgt sto den
       * når huset hadde konserter, eller var en ren arrangør — det er det backfillen ga dem.
       */
      {
        id: 'shows',
        labelKey: 'nav.shows',
        icon: 'music',
        to: '/shows',
        status: 'available',
        /* Modulen Konserter følger av ressursen. Byrå tar alltid med Konserter (253.58), så F24 er borte. */
        permission: 'concert:list',
        hiddenFor: ['guest'],
      },
      /*
       * MINE KONSERTER UNDER STREKEN (A24): personens egne, på tvers av organisasjoner, rett etter husets
       * Konserter. Vises bare når det finnes noe der (`onlyWith`), og står også i en personlig konto.
       */
      {
        id: 'concerts',
        personalAccount: true,
        labelKey: 'nav.myConcerts',
        icon: 'music',
        to: '/concerts',
        status: 'available',
        /* Gjestemodus (sak 232, T5): ingen faste menypunkter — gjesten navigerer i det delte. */
        hiddenFor: ['guest'],
        onlyWith: 'concerts',
      },
      // --- Phase 1 insertion point: house section ---
    ],
  },
  {
    id: 'festivals',
    /* «Festivaler»: spillestedene har fått sin egen seksjon under (SPILLESTED-KONSERT, agent A). */
    titleKey: 'nav.section.festivalList',
    source: 'festivals',
    entries: [],
    /* Modulen Festival (0483), spurt av can() med modulen som kontekst (G1). */
    permission: 'festival:list',
    inModule: 'festival',
    zone: 'house',
  },
  /*
   * SPILLESTED (planen §6): husets venue-beholder med sin egen undermeny — Oversikt, Kvelder,
   * Datoforespørsler, Saler og spesifikasjon, Personer, Ressurser, Innstillinger. Menyen bygges av
   * data: seksjonen finnes bare når huset har en rad av typen `venue`.
   */
  {
    id: 'venue',
    titleKey: 'nav.section.venue',
    source: 'venues',
    entries: [],
    permission: 'festival:list',
    inModule: 'venue',
    zone: 'house',
  },
  /*
   * CONTROL ROOM (sak 238) OG DELTE ARTISTER (A9) NEDERST UNDER STREKEN, i den rekkefølgen Atle ga
   * 22.10. Begge følger personen, ikke organisasjonen.
   */
  {
    id: 'platform',
    source: 'static',
    zone: 'house',
    entries: [
      {
        id: 'controlRoom',
        personalAccount: true,
        labelKey: 'nav.controlRoom',
        icon: 'controlRoom',
        to: '/control-room',
        status: 'available',
        onlyPlatformAdmin: true,
        /* Ingen fast oppføring for en gjest (sak 232) — en plattformadmin er aldri gjest. */
        hiddenFor: ['guest'],
      },
    ],
  },
  /*
   * DELTE ARTISTER (A9, 268.2 a): artistene et byrå har delt med deg (Gi innlogging), på tvers av
   * organisasjonene, og de delte bookingene deres. Dataene kommer fra delingen, aldri fra byråets egne
   * tabeller. Står bare når du har en levende artistdeling.
   */
  {
    id: 'shared',
    source: 'static',
    zone: 'house',
    entries: [
      {
        id: 'sharedArtists',
        personalAccount: true,
        labelKey: 'nav.sharedArtists',
        icon: 'booking',
        to: '/shared-artists',
        status: 'available',
        hiddenFor: ['guest'],
        onlyWith: 'sharedArtists',
      },
      /*
       * DELTE PRODUKSJONER (§5.1, Atle 27.10): det som er delt med meg fra organisasjoner jeg ikke er
       * medlem av, rett etter Delte artister. Står bare når noe er delt (`app.my_shared_productions()`).
       */
      {
        id: 'sharedProductions',
        personalAccount: true,
        labelKey: 'nav.sharedProductions',
        icon: 'festival',
        to: '/shared-productions',
        status: 'available',
        hiddenFor: ['guest'],
        onlyWith: 'sharedProductions',
      },
    ],
  },
] as const;

/** Every static entry, flattened — used by the hotkey registration and by the tests. */
export const NAV_ENTRIES: readonly NavEntry[] = NAV_SECTIONS.filter(
  (section) => section.source === 'static',
).flatMap((section) => section.entries);

export function navEntryById(id: string): NavEntry | undefined {
  return NAV_ENTRIES.find((entry) => entry.id === id);
}

/**
 * The industry vocabulary each area will handle, by nav id. It lives here, next to the menu,
 * because it is the same kind of thing: product structure expressed as data. The values are
 * `terms.*` keys, which `check-i18n` locks byte-identical across both locale files — the plan's
 * "ikke overoversett" rule, machine-enforced.
 */
export const NAV_AREA_TERMS: Readonly<Record<string, readonly TKey[]>> = {
  overview: ['terms.lineup', 'terms.pax', 'terms.curfew'],
  inbox: ['terms.advance', 'terms.rider', 'terms.tourManager'],
  tasks: ['terms.getIn', 'terms.loadIn', 'terms.loadOut', 'terms.soundcheck'],
  contacts: ['terms.headliner', 'terms.support', 'terms.tourManager'],
  /*
   * These three moved off «organization» when that entry was removed — it was never a page, and
   * NAVIGATION.md says so. Venue, FOH and monitor are words somebody meets inside an arrangement
   * rather than in a member list, which is where they should have been from the start.
   */
  /* «venue» er ikke lenger et fagord på engelsk i den norske fila: det heter spillested (253.60). */
  festivals: ['terms.foh', 'terms.monitor'],
  booking: ['terms.advance', 'terms.settlement', 'terms.lineup', 'terms.headliner'],
  documents: ['terms.rider', 'terms.backline', 'terms.hospitality', 'terms.settlement'],
};
